import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import {
  Link,
  useNavigate,
  useLocation,
  Outlet,
} from 'react-router-dom';
import {
  UserOutlined,
  LogoutOutlined,
  EditOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  Tag,
  Layout,
  Menu,
  Avatar,
  Drawer,
  Typography,
  Alert,
} from 'antd';
import GlobalStyles from 'themes/global';
import { defSigob } from 'themes';
import { getRoutes } from 'routes/index';
import { foo } from 'mocks/hardcodedStuff';
import Bread from 'components/Layout/Bread';
import Footer from 'components/Layout/Footer';
import ThemeConfig from 'components/Layout/ThemeConfig';
import LayoutHeader from 'components/Layout/LayoutHeader';
import ModalChangePassword from 'components/Layout/ModalChangePassword';
import ModalChangeFiscalPeriod from 'components/Layout/ModalChangeFiscalPeriod';
import ModalWorking from 'components/ModalWorking';
import {
  setCollapsedSidebar,
  resetApp,
  selectTheme,
  selectCollapsedSidebar,
  selectSidebarHidden,
  selectMenuOrder,
  selectBloqueosCalendario,
  setBloqueosCalendario,
} from 'store/app';
import { getBloqueosCalendario } from 'api/nominas/calendarioDeNominas';
import { bloqueosDeRuta } from 'utils/bloqueoCalendario';
import {
  withFullPaths,
  filterHiddenFromTree,
  applyVisualOrder,
  findVisualAncestorChain,
  collectOpenByDefaultPaths,
} from 'utils/sidebarMenuConfig';
import {
  handleLogout,
  selectPeriodoFiscalSelected,
  selectRoles,
  selectUser,
} from 'store/auth';
import { clearCatalogs } from 'store/catalogos';
import {
  clearPersistedTableFilters,
  clearPersistedTableFiltersForPath,
} from 'utils/tableFiltersStorage';
import 'components/Layout/index.scss';

const { Content, Sider } = Layout;
const { Text } = Typography;

/**
 * Renderiza un valor de texto que puede venir como string plano o como objeto
 * de catalogo ({ id, descripcion, estados_globales, ... }) segun el endpoint
 * que haya alimentado a `user` -- evita "Objects are not valid as a React
 * child" cuando llega el objeto completo en vez del string ya extraido.
 */
const renderizarTextoOSubcampo = (valor) => {
  if (valor && typeof valor === 'object') {
    return valor.descripcion ?? '';
  }
  return valor;
};

const AppLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const roles = useSelector(selectRoles);
  const theme = useSelector(selectTheme);

  const periodoFiscalSelected = useSelector(selectPeriodoFiscalSelected);
  const collapsedSidebar = useSelector(selectCollapsedSidebar);
  const sidebarHidden = useSelector(selectSidebarHidden);
  const menuOrder = useSelector(selectMenuOrder);
  const bloqueosCalendario = useSelector(selectBloqueosCalendario);
  const bloqueosDeEstaRuta = bloqueosDeRuta(bloqueosCalendario, location.pathname);
  const [collapsed, setCollapsed] = useState(collapsedSidebar);
  const [isMobile, setIsMobile] = useState(false);

  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [visibleWorking, setVisibleWorking] = useState(false);
  const [visiblePassword, setVisiblePassword] = useState(false);

  const [margin, setMargin] = useState();
  const [drawerContent, setDrawerContent] = useState();
  const [drawerTitle, setDrawerTitle] = useState();
  const [collapsedWidth, setCollapsedWidth] = useState(80);

  const [selectedKeys, setSelectedKeys] = useState([]);
  // selectedKeys/openKeys del <Menu> del sidebar: se calculan sobre el árbol VISUAL
  // (con módulos ocultos y orden aplicados), no sobre el prefijo de la URL — una página
  // movida a otro grupo visual sigue resaltando/abriendo su grupo visual actual, aunque
  // su URL real (usada por Bread para el breadcrumb, sin tocar) no coincida con ese grupo.
  const [menuSelectedKeys, setMenuSelectedKeys] = useState([]);
  const [menuOpenKeys, setMenuOpenKeys] = useState([]);

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    const splitedPath = currentPath.split('/').filter((x) => x.length);
    return splitedPath.map((s, is) => `/${splitedPath.filter((a, i) => i <= is).join('/')}`);
  };

  useEffect(() => {
    const selected = getSelectedKeys();
    setSelectedKeys(selected);
    // eslint-disable-next-line
  }, [location]);

  const previousPathnameRef = useRef(location.pathname);
  // Base de comparación para el submenú flotante del sidebar colapsado (ver useEffect
  // de visibilitychange/blur y handleSiderMouseLeave más abajo): a qué keys debe volver
  // cuando hay que "podar" un submenú que quedó abierto de forma anómala.
  const baselineOpenKeysRef = useRef([]);

  // Los filtros de <Table> se persisten en sessionStorage por pantalla (ver components/Table.jsx).
  // Al cambiar de menú (pathname distinto) se limpian los filtros de la pantalla que se abandona,
  // para que solo se mantengan al navegar dentro de la misma página/componentes de ese menú.
  // Calendario de Nóminas: funcionalidades cerradas hoy. Se consulta en cada cambio de
  // pantalla para avisar y deshabilitar captura (components/Table); el backend es quien
  // realmente rechaza las escrituras.
  useEffect(() => {
    let mounted = true;
    getBloqueosCalendario().then((bloqueos) => {
      if (mounted) dispatch(setBloqueosCalendario(bloqueos));
    });
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    if (previousPathname !== location.pathname) {
      clearPersistedTableFiltersForPath(previousPathname);
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile) {
      setMargin(0);
      setCollapsedWidth(0);
      setCollapsed(true);
      dispatch(setCollapsedSidebar(true));
    } else {
      setMargin(collapsedSidebar ? 80 : 300);
      setCollapsedWidth(80);
    }
    // eslint-disable-next-line
  }, [isMobile]);

  const User = () => (
    <div className="user-info">
      <Row>
        <Row className="avatar-wrapper">
          {/* eslint-disable-next-line no-use-before-define */}
          {UserAvatar(false)}
        </Row>
        <Col
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text strong>
            {`${user?.first_name || ''} ${user?.last_name || ''} ${user?.second_last_name || ''}`}
          </Text>
          <Text>
            {user?.email}
          </Text>

          {!!roles?.length && (
            <Col className="card">
              <Text style={{
                fontSize: 16,
                marginBottom: 5,
              }}
              >
                Roles
              </Text>
              <Row>
                {roles.map((role) => (
                  <React.Fragment key={role.id}>
                    <Tag>
                      {role.name.split('-').shift().trim()}
                    </Tag>
                  </React.Fragment>
                ))}
              </Row>
            </Col>
          )}

          <Col className="card">
            <Text style={{
              fontSize: 16,
              marginBottom: 5,
            }}
            >
              Información del usuario
            </Text>
            {!!user?.unidad_responsable && (
              <Text>
                Unidad responsable:
                {' '}
                {renderizarTextoOSubcampo(user?.unidad_responsable)}
              </Text>
            )}
            {!!user?.funcionario?.puesto && (
              <Text>
                Puesto:
                {' '}
                {renderizarTextoOSubcampo(user?.funcionario.puesto)}
              </Text>
            )}
            {!!user?.funcion && (
              <Text style={{ textAlign: 'center' }}>
                Función:
                {' '}
                {renderizarTextoOSubcampo(user?.funcion)}
              </Text>
            )}
            {!!user?.justificacion && (
              <Text style={{ textAlign: 'center' }}>
                Justificación:
                {' '}
                {renderizarTextoOSubcampo(user?.justificacion)}
              </Text>
            )}
          </Col>

          <Row className="card">
            <Button
              type="link"
              onClick={() => {
                setVisibleWorking(true);
                setVisibleDrawer(false);
              }}
            >
              <UserOutlined />
              Perfil de Usuario
            </Button>
            <Button
              type="link"
              className="update-password no-color"
              onClick={() => {
                setVisiblePassword(true);
                setVisibleDrawer(false);
              }}
            >
              <EditOutlined />
              Actualizar Contraseña
            </Button>
            <Button
              type="link"
              danger
              className="logout no-color"
              onClick={() => {
                dispatch(handleLogout());
                dispatch(resetApp());
                dispatch(clearCatalogs());
                clearPersistedTableFilters();
                navigate('/inicio-de-sesion');
              }}
            >
              <LogoutOutlined />
              Cerrar sesión
            </Button>
          </Row>
          <br />
          <br />
          {periodoFiscalSelected && (
            <ModalChangeFiscalPeriod />
          )}
          <Text>
            Ver.
            {process.env.REACT_APP_VERSION}
          </Text>
        </Col>
      </Row>
    </div>
  );

  const onClickAvatar = () => {
    setDrawerContent(User);
    setDrawerTitle('Usuario');
    setVisibleDrawer(true);
  };

  const onClickSetting = () => {
    setDrawerContent(<ThemeConfig />);
    setVisibleDrawer(true);
    setDrawerTitle('Configuración de tema');
  };

  const notifications = () => (foo.map((item) => (
    <Row
      key={item.id}
      className={`notification-item ${item.readed ? 'readed' : ''}`}
    >
      <Col>
        <Text style={{ color: '#000' }} strong>
          {item.title}
        </Text>
        <br />
        <Text>
          {item.content}
        </Text>
        <br />
        <Text>{item.date}</Text>
      </Col>
    </Row>
  )));

  const onClickNotifications = () => {
    setDrawerContent(notifications);
    setDrawerTitle('Notificaciones');
    // Cuando el modal de notificaciones funcione, cambiar SetVisibleWorking -> setVisibleDrawer
    setVisibleWorking(true);
  };

  const UserAvatar = () => (
    <Avatar
      src={user?.foto}
      icon={user?.foto ? null : <UserOutlined />}
      size="large"
    />
  );

  const LinkTo = ({ to, title }) => (
    <Link to={to}>
      {title}
    </Link>
  );

  LinkTo.propTypes = {
    to: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  };

  const toggle = () => {
    setCollapsed(!collapsed);
    dispatch(setCollapsedSidebar(!collapsed));
    if (isMobile) {
      setMargin(0);
      setCollapsedWidth(0);
    } else {
      setMargin(!collapsed ? 80 : 300);
      setCollapsedWidth(!collapsed ? 80 : 300);
    }
  };

  // route.fullPath ya viene congelado desde withFullPaths() sobre el árbol real — nunca
  // se recalcula aquí a partir de dónde quedó anidado visualmente el nodo.
  const getMenuItems = (_routes) => _routes.map((route) => {
    const { children: routeChildren, fullPath } = route;
    // sidebarTitle permite un nombre corto en el menú sin cambiar el título de la pantalla
    // (el título define la URL, ver routes/index.js).
    const title = route.sidebarTitle || route.title;
    const Icon = route.icon ? <route.icon /> : <span />;

    if (routeChildren?.length) {
      return {
        key: fullPath,
        icon: Icon,
        label: title,
        children: [
          ...getMenuItems(routeChildren),
        ],
      };
    }
    return {
      key: fullPath,
      icon: Icon,
      onClick: () => isMobile && toggle(),
      label: <LinkTo to={fullPath} title={title} />,
    };
  });

  const _routes = getRoutes();
  const routes = periodoFiscalSelected?.vigente ? _routes : _routes.filter((e, i) => i !== 2);
  const visibleTree = filterHiddenFromTree(withFullPaths(routes), sidebarHidden);
  const visualTree = applyVisualOrder(visibleTree, menuOrder);

  useEffect(() => {
    // Los grupos marcados como "pin" (openByDefault) en el configurador de sidebar, y el
    // grupo de la página actual, se mantienen expandidos SOLO con el Sider expandido (ahí
    // los submenús se abren en línea, dentro de la propia barra, y pueden convivir varios
    // a la vez sin problema). Con el Sider colapsado, antd dibuja cada key de `openKeys`
    // como su PROPIO flyout flotante posicionado sobre su icono del riel — si dos o más
    // quedan abiertas a la vez (p. ej. un grupo fijado + el de la página actual, casi
    // siempre grupos distintos) los flyouts se dibujan encimados e ilegibles. Por eso,
    // colapsado, arrancamos sin nada forzado abierto: el usuario abre el que necesita al
    // pasar el mouse, vía onOpenChange del propio <Menu>.
    const pinnedKeys = collectOpenByDefaultPaths(visualTree);
    const ancestorChain = findVisualAncestorChain(visualTree, location.pathname);
    const chainForSelection = ancestorChain || getSelectedKeys();
    setMenuSelectedKeys(chainForSelection);
    const baseline = collapsed
      ? []
      : [...new Set([...pinnedKeys, ...chainForSelection.slice(0, -1)])];
    setMenuOpenKeys(baseline);
    baselineOpenKeysRef.current = baseline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sidebarHidden, menuOrder, periodoFiscalSelected, collapsed]);

  // El popup del submenú flotante (rc-trigger, activado por antd cuando el Sider está
  // colapsado) solo se cierra cuando detecta un mouseleave real. Si el usuario cambia de
  // pestaña/ventana con el submenú abierto no se dispara ningún evento de mouse, así que
  // se queda "congelado" visible hasta el siguiente cambio de ruta. Al recuperar el foco
  // o la visibilidad, se poda de vuelta a las keys base (fijadas + ruta actual).
  useEffect(() => {
    const resetToBaseline = () => setMenuOpenKeys(baselineOpenKeysRef.current);
    const onVisibilityChange = () => {
      if (document.hidden) resetToBaseline();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', resetToBaseline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', resetToBaseline);
    };
  }, []);

  // rc-menu (debajo de <Menu>) NO garantiza que solo una rama del riel colapsado esté
  // abierta a la vez, a NINGÚN nivel de anidamiento: cada submenú (a cualquier profundidad)
  // cierra su propio popup por su cuenta, con un timer interno (subMenuCloseDelay) disparado
  // por su propio mouseleave. Si ese timer no llega a disparar (p. ej. el usuario mueve el
  // cursor rápido entre hermanos — Incidencias -> Liquidaciones dentro de "Procesos" — o un
  // re-render de este componente, frecuente aquí por las actualizaciones de WebSocket, ocurre
  // justo mientras el mouse está en tránsito), la key del hermano anterior nunca se quita del
  // arreglo, y el siguiente hover simplemente AGREGA la suya, dejando varios flyouts
  // amontonados uno sobre otro (visto tanto entre grupos raíz como entre sub-submenús).
  //
  // rc-menu siempre agrega la key recién abierta al FINAL del arreglo (ver rc-menu/es/Menu.js:
  // `newOpenKeys.push(key)`), así que la última entrada es siempre el grupo que se está
  // hovering ahora mismo. En vez de intentar podar hermanos sueltos nivel por nivel,
  // reconstruimos la cadena de ancestros completa de ESE grupo con nuestro propio
  // findVisualAncestorChain() (misma fuente de verdad que ya usa el useEffect de arriba) y
  // reemplazamos el arreglo completo por esa cadena — así solo puede sobrevivir una rama
  // limpia, sin importar cuántos hermanos sueltos de cualquier profundidad se hayan quedado
  // pegados. Con el sidebar expandido (inline, sin popups) esto no aplica: varios grupos
  // pueden convivir expandidos sin problema visual.
  const handleMenuOpenChange = (keys) => {
    if (!collapsed || !keys.length) {
      setMenuOpenKeys(keys);
      return;
    }
    const grupoActivo = keys[keys.length - 1];
    const cadenaCompleta = findVisualAncestorChain(visualTree, grupoActivo);
    setMenuOpenKeys(cadenaCompleta || keys);
  };

  // Respaldo ante fallos de detección de rc-trigger (p. ej. movimientos rápidos del mouse
  // entre el riel de iconos y el popup): si tras salir del Sider el cursor no quedó sobre
  // el propio Sider ni sobre el popup del submenú, se poda a las keys base.
  const handleSiderMouseLeave = () => {
    window.setTimeout(() => {
      const stillHovering = document.querySelector('#left-sider:hover, .ant-menu-submenu-popup:hover');
      if (!stillHovering) {
        setMenuOpenKeys(baselineOpenKeysRef.current);
      }
    }, 150);
  };

  return (
    <ThemeProvider theme={theme || defSigob}>
      <GlobalStyles />
      <Layout>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          collapsedWidth={collapsedWidth}
          id="left-sider"
          theme="dark"
          width={300}
          breakpoint="md"
          onBreakpoint={(broken) => {
            setIsMobile(broken);
          }}
          onMouseLeave={handleSiderMouseLeave}
          style={{
            position: 'absolute',
            top: 64,
            left: 0,
          }}
        >
          <Menu
            id="side-menu"
            mode="inline"
            selectedKeys={menuSelectedKeys}
            openKeys={menuOpenKeys}
            onOpenChange={handleMenuOpenChange}
            items={getMenuItems(visualTree)}
          />
        </Sider>
        <Layout
          className="site-layout"
          style={{ marginLeft: collapsed ? margin : 300, transition: 'all 0.2s' }}
        >
          <LayoutHeader
            isMobile={isMobile}
            toggle={toggle}
            collapsed={collapsed}
            onClickAvatar={onClickAvatar}
            onClickNotifications={onClickNotifications}
            onClickSetting={onClickSetting}
          />
          <Content
            style={{
              left: margin,
              width: `calc(100vw - ${margin}px)`,
              padding: '15px 25px 0 25px',
            }}
          >
            <Bread
              routes={routes}
              isMobile={isMobile}
              currentPath={selectedKeys}
            />
            {bloqueosDeEstaRuta.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message={bloqueosDeEstaRuta.map((bloqueo) => bloqueo.mensaje).join(' ')}
              />
            )}
            <div
              className="customCard"
              style={{
                height: `calc(100vh - ${(isMobile ? 140 : 175) + (bloqueosDeEstaRuta.length ? 48 : 0)}px)`,
                // El scroll real de cada vista lo maneja su propio contenido acotado (el
                // <Table> compartido mide su alto real contra este mismo nodo — ver
                // Table.jsx —, o el `.ant-card-body`/`.ant-tabs-content-holder` de un
                // Card/Tabs con su propio alto y overflow). Si customCard TAMBIÉN
                // scrollea, aparece un segundo scrollbar aquí, pegado al borde de la
                // tarjeta, redundante con el de la tabla/card interno.
                // Excepción: vistas de contenido libre sin Table ni Card acotado (ej.
                // Dashboard, con widgets arrastrables) no tienen ningún hijo que scrollee
                // por su cuenta — esas gestionan su propio scroll local con la clase
                // `free-scroll-page` (ver Dashboard/index.jsx e index.scss) en vez de
                // depender de este nodo.
                overflow: 'hidden',
                minHeight: 230,
                marginBottom: 17,
              }}
            >
              <Outlet />
            </div>
            <Footer editable />
          </Content>
          <Drawer
            className="layout-drawer"
            title={drawerTitle}
            placement="right"
            width={isMobile ? '100%' : 400}
            destroyOnClose
            onClose={() => {
              setVisibleDrawer(false);
            }}
            open={visibleDrawer}
            closable
          >
            {drawerContent}
          </Drawer>
        </Layout>
      </Layout>
      <ModalWorking
        open={visibleWorking}
        setVisible={setVisibleWorking}
      />
      <ModalChangePassword
        open={visiblePassword}
        setVisible={setVisiblePassword}
      />
    </ThemeProvider>
  );
};

export default AppLayout;
